import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SessionSummary from '../components/SessionSummary';
import { Clock, Lightbulb, X, CheckCircle2, XCircle, ChevronRight, AlertTriangle, Music, Disc3, Sparkles } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const QUIZ_DURATION = 600; // 10 minutes in seconds

// Category configuration
const CATEGORY_CONFIG = {
  logic: { name: 'Logic', icon: '🧠', color: 'bg-purple-100 text-purple-700' },
  pattern: { name: 'Pattern', icon: '🔁', color: 'bg-blue-100 text-blue-700' },
  memory: { name: 'Memory', icon: '🧩', color: 'bg-green-100 text-green-700' },
  focus: { name: 'Focus', icon: '🎯', color: 'bg-orange-100 text-orange-700' },
  speed: { name: 'Speed', icon: '⚡', color: 'bg-yellow-100 text-yellow-700' },
  paradoxes: { name: 'Paradoxes', icon: '♾️', color: 'bg-indigo-100 text-indigo-700' },
  detective: { name: 'Detective', icon: '🔍', color: 'bg-rose-100 text-rose-700' },
  gaming_trivia: { name: 'Gaming', icon: '🎮', color: 'bg-cyan-100 text-cyan-700' },
  science_tech: { name: 'Science & Tech', icon: '🔬', color: 'bg-teal-100 text-teal-700' },
  creative_problem: { name: 'Creative', icon: '🧩', color: 'bg-lime-100 text-lime-700' },
  data_stats: { name: 'Data & Stats', icon: '📊', color: 'bg-sky-100 text-sky-700' },
  logic_puzzles: { name: 'Logic Puzzles', icon: '🧩', color: 'bg-violet-100 text-violet-700' },
  lateral_thinking: { name: 'Lateral Thinking', icon: '🤔', color: 'bg-fuchsia-100 text-fuchsia-700' },
  word_anagram: { name: 'Word Anagram', icon: '🔤', color: 'bg-orange-100 text-orange-700' },
  animal_logic: { name: 'Animal Logic', icon: '🦁', color: 'bg-green-100 text-green-700' },
  brain_teaser: { name: 'Brain Teaser', icon: '🧠', color: 'bg-red-100 text-red-700' },
  rebus_puzzle: { name: 'Rebus Puzzle', icon: '🖼️', color: 'bg-amber-100 text-amber-700' },
  spot_the_fake_rule: { name: 'Spot the Fake', icon: '🚫', color: 'bg-rose-100 text-rose-700' },
  wrong_answer_on_purpose: { name: 'Wrong on Purpose', icon: '🙃', color: 'bg-pink-100 text-pink-700' },
  emoji_story_decode: { name: 'Emoji Decode', icon: '🎭', color: 'bg-yellow-100 text-yellow-700' },
  one_word_changed: { name: 'Word Swap', icon: '🔀', color: 'bg-cyan-100 text-cyan-700' },
  mini_detective_scene: { name: 'Mini Detective', icon: '🕵️', color: 'bg-indigo-100 text-indigo-700' },
  optical_illusion_logic: { name: 'Optical Illusion', icon: '👁️', color: 'bg-violet-100 text-violet-700' },
  idiom_origins: { name: 'Idiom Origins', icon: '💬', color: 'bg-blue-100 text-blue-700' },
  probability_trap: { name: 'Probability', icon: '🎲', color: 'bg-rose-100 text-rose-700' },
  deductive_logic: { name: 'Deductive Logic', icon: '🔗', color: 'bg-purple-100 text-purple-700' },
  odd_one_out: { name: 'Odd One Out', icon: '🎯', color: 'bg-orange-100 text-orange-700' },
  cause_effect: { name: 'Cause & Effect', icon: '⚡', color: 'bg-teal-100 text-teal-700' },
  story_inference: { name: 'Story Inference', icon: '📖', color: 'bg-emerald-100 text-emerald-700' },
  math_logic: { name: 'Math Logic', icon: '🔢', color: 'bg-blue-100 text-blue-700' },
  fresh: { name: 'Fresh!', icon: '✨', color: 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700' }
};

const SessionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { api, refreshUser, user } = useAuth();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [showSummary, setShowSummary] = useState(false);
  const [coinsEarned, setCoinsEarned] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [mentorMessage, setMentorMessage] = useState("Let's get started!");
  
  // Timer state
  const [remainingTime, setRemainingTime] = useState(QUIZ_DURATION);
  const [timeExpired, setTimeExpired] = useState(false);
  const timerEndTimeRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Beat Yesterday
  const [yesterdayScore, setYesterdayScore] = useState(null);
  const [showBeatYesterday, setShowBeatYesterday] = useState(false);
  const [beatYesterdayBonus, setBeatYesterdayBonus] = useState(0);

  // Question state - initialize as null/false to prevent auto-highlight
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [questionLoadTime, setQuestionLoadTime] = useState(Date.now());
  
  // Memory game state
  const [memoryCards, setMemoryCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [memoryPhase, setMemoryPhase] = useState('show'); // show, play, or for recall: memorize, answer
  const [memoryTimer, setMemoryTimer] = useState(0);
  const [memoryGameComplete, setMemoryGameComplete] = useState(false);
  const [isCheckingMatch, setIsCheckingMatch] = useState(false);
  
  // First time financial mission note state - show during loading for new users (per-user)
  const userId = user?.id || '';
  const [showFinancialMission, setShowFinancialMission] = useState(false);
  const [fmChecked, setFmChecked] = useState(false);

  // Check financial mission flag once user ID is available
  useEffect(() => {
    if (userId) {
      // Check both old (global) and new (per-user) keys for backwards compat
      const seenNew = localStorage.getItem(`braingym_financial_mission_seen_${userId}`);
      const seenOld = localStorage.getItem('braingym_financial_mission_seen');
      const seen = seenNew || seenOld;
      if (seen) {
        // Migrate old key to new key
        if (!seenNew && seenOld) {
          localStorage.setItem(`braingym_financial_mission_seen_${userId}`, 'true');
        }
        setShowFinancialMission(false);
      } else {
        setShowFinancialMission(true);
      }
      setFmChecked(true);
    }
  }, [userId]);

  // DJ Mode state
  const [showDjMode, setShowDjMode] = useState(false);
  const [djCategories, setDjCategories] = useState([]);
  const [djPicks, setDjPicks] = useState([]);
  const [djLoading, setDjLoading] = useState(false);
  
  // Handle dismissing financial mission → show DJ mode next if available, or reveal quiz
  const handleDismissFinancialMission = () => {
    localStorage.setItem(`braingym_financial_mission_seen_${userId}`, 'true');
    setShowFinancialMission(false);
    
    // If DJ categories were pre-fetched, show DJ mode now
    if (djCategories.length >= 6) {
      setShowDjMode(true);
    }
    // Otherwise quiz is already loaded underneath — it just becomes visible
  };

  // DJ Mode handlers
  const handleDjToggle = (categoryId) => {
    setDjPicks(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      }
      if (prev.length >= 3) return prev;
      return [...prev, categoryId];
    });
  };

  const handleDjConfirm = async () => {
    if (djPicks.length !== 3) return;
    setDjLoading(true);
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`braingym_dj_used_${userId}_${today}`, 'true');
    setShowDjMode(false);
    // If session wasn't loaded yet (new session), load with DJ picks
    if (!session) {
      setLoading(true);
      await loadSession(djPicks);
    }
    setDjLoading(false);
  };

  const handleDjSkip = async () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`braingym_dj_used_${userId}_${today}`, 'true');
    setShowDjMode(false);
    // If session wasn't loaded yet, load normally
    if (!session) {
      setLoading(true);
      await loadSession([]);
    }
  };
  
  // Ref to always have latest memoryCards value
  const memoryCardsRef = useRef([]);
  useEffect(() => {
    memoryCardsRef.current = memoryCards;
  }, [memoryCards]);

  useEffect(() => {
    // Always load session immediately — FM and DJ mode render as overlays on top
    loadSession();
  }, []);

  // DJ mode: check after userId is available and FM check is done
  useEffect(() => {
    if (!fmChecked || !userId) return;
    checkDjMode();
  }, [fmChecked, userId]);

  const checkDjMode = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const djKey = `braingym_dj_used_${userId}_${today}`;
      if (localStorage.getItem(djKey)) return;

      const djResp = await api.get('/session/dj-categories');
      if (!djResp.data.session_exists && djResp.data.categories && djResp.data.categories.length >= 6) {
        setDjCategories(djResp.data.categories);
        // Only show DJ if FM is not currently showing (FM takes priority)
        if (!showFinancialMission) {
          setShowDjMode(true);
        }
      }
    } catch (e) {
      // DJ fetch failed — quiz loads normally
    }
  };

  // CRITICAL: Reset selection state immediately when question changes
  // This ensures no pre-selected answers appear on new questions
  useEffect(() => {
    // Reset selection-related state synchronously when question changes
    setSelectedAnswer(null);
    setShowResult(false);
    setIsCorrect(false);
    setShowHint(false);
    setIsSubmitting(false);
    setQuestionStartTime(Date.now());
    setQuestionLoadTime(Date.now());
    setHintsUsed(0);
    
    // Note: Memory game state is reset and re-initialized in its own useEffect
  }, [currentQuestion]);

  // Countdown timer - syncs with stored endTime for persistence
  useEffect(() => {
    if (!session || showSummary || timeExpired) return;

    const timer = setInterval(() => {
      if (!timerEndTimeRef.current) return;
      const remaining = Math.max(0, Math.floor((timerEndTimeRef.current - Date.now()) / 1000));
      setRemainingTime(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setTimeExpired(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [session, showSummary, timeExpired]);

  // Handle timer expiry - auto-complete session
  useEffect(() => {
    if (!timeExpired || !session) return;

    const handleTimeout = async () => {
      try {
        const response = await api.post(`/session/${session.id}/timeout`);
        setCoinsEarned(response.data.coins_earned);
      } catch (error) {
        console.error('Timeout error:', error);
      } finally {
        if (sessionIdRef.current) {
          localStorage.removeItem(`braingym_timer_end_${sessionIdRef.current}`);
        }
        await refreshUser();
        setShowSummary(true);
      }
    };

    handleTimeout();
  }, [timeExpired, session]);

  const loadSession = async (djPicksOverride = null) => {
    try {
      const body = djPicksOverride && djPicksOverride.length > 0 ? { dj_picks: djPicksOverride } : {};
      const response = await api.post('/session/start', body);
      if (response.data.can_play === false) {
        toast.success(response.data.message);
        navigate('/dashboard');
        return;
      }
      
      const sessionData = response.data.session;
      setSession(sessionData);
      setCurrentQuestion(sessionData.current_question || 0);
      sessionIdRef.current = sessionData.id;

      // Beat Yesterday score
      const hasYesterdayScore = !!response.data.yesterday_score;
      if (hasYesterdayScore) {
        setYesterdayScore(response.data.yesterday_score);
      }

      // Initialize persistent timer
      const timerKey = `braingym_timer_end_${sessionData.id}`;
      let endTime;
      const storedEndTime = localStorage.getItem(timerKey);

      if (storedEndTime && response.data.resumed) {
        endTime = parseInt(storedEndTime, 10);
        const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
        if (remaining <= 0) {
          setRemainingTime(0);
          setTimeExpired(true);
          timerEndTimeRef.current = endTime;
          setLoading(false);
          return;
        }
        setRemainingTime(remaining);
      } else {
        endTime = Date.now() + QUIZ_DURATION * 1000;
        localStorage.setItem(timerKey, endTime.toString());
        setRemainingTime(QUIZ_DURATION);
      }

      timerEndTimeRef.current = endTime;

      // Show Beat Yesterday popup during loading for 3 seconds, then start quiz
      if (hasYesterdayScore) {
        setShowBeatYesterday(true);
        setTimeout(() => {
          setShowBeatYesterday(false);
          setLoading(false);
        }, 3000);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Could not start session. Try again!');
      navigate('/dashboard');
      setLoading(false);
    }
  };

  const submitAnswer = useCallback(async (questionId, answer, timeTaken, hUsed = 0) => {
    if (!session) return null;
    if (isSubmitting) return null; // Prevent double submissions

    setIsSubmitting(true);
    
    try {
      const response = await api.post(`/session/${session.id}/answer`, {
        question_id: questionId,
        answer,
        time_taken: timeTaken,
        hints_used: hUsed,
      });

      const result = response.data;
      
      // Update mentor message
      if (result.correct) {
        const messages = [
          "Great job! You're getting better!",
          "Awesome! Keep up the amazing work!",
          "You nailed it! Brain power unlocked!",
          "Fantastic thinking!",
        ];
        setMentorMessage(messages[Math.floor(Math.random() * messages.length)]);
      } else {
        const messages = [
          "Nice try! Let's keep going!",
          "That's okay! Every try makes you smarter!",
          "Good effort! The next one's yours!",
          "You're learning! That's what matters!",
        ];
        setMentorMessage(messages[Math.floor(Math.random() * messages.length)]);
      }

      if (result.session_complete) {
        // Clear persistent timer
        if (sessionIdRef.current) {
          localStorage.removeItem(`braingym_timer_end_${sessionIdRef.current}`);
        }
        setCoinsEarned(result.coins_earned);
        if (result.beat_yesterday_bonus) setBeatYesterdayBonus(result.beat_yesterday_bonus);

        // Quiz complete - go straight to summary
        setShowSummary(true);
        await refreshUser();
      } else {
        setAnswers(prev => [...prev, { correct: result.correct }]);
        // Show result for 1.5 seconds then auto-advance
        setTimeout(() => {
          setCurrentQuestion(result.next_question);
        }, 1500);
      }

      return result;
    } catch (error) {
      console.error('Submit answer error:', error);
      toast.error('Something went wrong. Try again!');
      setIsSubmitting(false);
      return null;
    }
  }, [session, api, refreshUser, isSubmitting]);

  // Boss question answer handler
  const handleAnswerSelect = (answer) => {
    if (showResult || isSubmitting) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null || showResult || isSubmitting) return;
    
    const question = session.questions[currentQuestion];
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
    
    const result = await submitAnswer(question.id, selectedAnswer, timeTaken, hintsUsed);
    
    if (result) {
      setIsCorrect(result.correct);
      setShowResult(true);
      // Auto-advance after showing result is handled by submitAnswer's setTimeout
    }
  };

  const handleNextQuestion = () => {
    // Move handled by submitAnswer's next_question
    // State reset is already handled by the useEffect on currentQuestion change
  };

  const handleShowHint = () => {
    if (hintsUsed < 3) {
      setHintsUsed(prev => prev + 1);
      setShowHint(true);
    }
  };

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = async () => {
    // Progress is automatically saved on each answer submission
    // Just navigate back to dashboard
    setShowExitConfirm(false);
    navigate('/dashboard');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Memory Grid Game Handlers
  const initializeMemoryGrid = (cards) => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setMemoryCards(shuffled.map((card, idx) => ({ id: idx, value: card, flipped: true })));
    setFlippedCards([]);
    setMatchedPairs([]);
    setMemoryPhase('show');
    
    // Show cards for 3 seconds then hide
    setTimeout(() => {
      setMemoryCards(prev => prev.map(c => ({ ...c, flipped: false })));
      setMemoryPhase('play');
    }, 3000);
  };

  const handleCardFlip = (cardId) => {
    if (memoryPhase !== 'play') return;
    if (isCheckingMatch) return; // Block clicks while checking match
    if (flippedCards.length >= 2) return;
    if (matchedPairs.includes(cardId)) return;
    if (flippedCards.includes(cardId)) return;
    if (memoryGameComplete) return;

    // Get the current cards from ref (has all card values)
    const currentCards = memoryCardsRef.current;
    const clickedCard = currentCards.find(c => c.id === cardId);
    if (!clickedCard) return;

    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);
    
    // Update card to show flipped state
    setMemoryCards(prevCards => 
      prevCards.map(c => c.id === cardId ? { ...c, flipped: true } : c)
    );
    
    // Check for match if we have 2 flipped cards
    if (newFlipped.length === 2) {
      setIsCheckingMatch(true);
      const [firstId, secondId] = newFlipped;
      const firstCard = currentCards.find(c => c.id === firstId);
      const secondCard = currentCards.find(c => c.id === secondId);
      
      if (firstCard && secondCard && firstCard.value === secondCard.value) {
        // Match found!
        const newMatchedPairs = [...matchedPairs, firstId, secondId];
        
        // Small delay to show matched state
        setTimeout(() => {
          setMatchedPairs(newMatchedPairs);
          setFlippedCards([]);
          setIsCheckingMatch(false);
          
          // Check if game complete
          const question = session.questions[currentQuestion];
          const totalPairs = question.pairs_count || Math.floor(question.cards.length / 2);
          const completedPairs = newMatchedPairs.length / 2;
          
          if (completedPairs >= totalPairs) {
            // Game complete - prevent further interaction and submit
            setMemoryGameComplete(true);
            const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
            
            // Submit the answer - state reset will be handled by useEffect when currentQuestion changes
            submitAnswer(question.id, { completed: true, pairs_found: completedPairs }, timeTaken, hintsUsed);
          }
        }, 500);
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setMemoryCards(prev => prev.map(c => 
            (c.id === firstId || c.id === secondId) ? { ...c, flipped: false } : c
          ));
          setFlippedCards([]);
          setIsCheckingMatch(false);
        }, 1000);
      }
    }
  };

  // Initialize memory game when question changes
  useEffect(() => {
    if (!session?.questions?.[currentQuestion]) return;
    
    const question = session.questions[currentQuestion];
    
    // Only initialize memory games
    if (question.type === 'memory_grid' && question.cards) {
      console.log('Initializing memory grid game for question', currentQuestion);
      
      // Initialize memory grid game
      const shuffled = [...question.cards].sort(() => Math.random() - 0.5);
      const initialCards = shuffled.map((card, idx) => ({ id: idx, value: card, flipped: true }));
      
      // Set initial state - cards are visible
      setMemoryCards(initialCards);
      setFlippedCards([]);
      setMatchedPairs([]);
      setMemoryPhase('show');
      setMemoryGameComplete(false);
      setIsCheckingMatch(false);
      
      // After 3 seconds, hide cards and start play phase
      const timer = setTimeout(() => {
        console.log('Memory grid: hiding cards, starting play phase');
        setMemoryCards(prev => prev.map(c => ({ ...c, flipped: false })));
        setMemoryPhase('play');
      }, 3000);
      
      return () => clearTimeout(timer);
    } else if (question.type === 'memory_recall') {
      console.log('Initializing memory recall game for question', currentQuestion);
      
      setMemoryPhase('memorize');
      setMemoryTimer(question.display_time || 5);
      setMemoryGameComplete(false);
      
      // Start countdown
      const interval = setInterval(() => {
        setMemoryTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setMemoryPhase('answer');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [currentQuestion, session]);

  // Watchdog: If question doesn't load or state is stuck for 5+ seconds, handle gracefully
  useEffect(() => {
    const watchdog = setTimeout(() => {
      // If we're stuck on a question with no valid state, try to recover
      if (session && !loading && !showSummary) {
        const question = session?.questions?.[currentQuestion];
        const totalQuestions = session?.total_questions || session?.questions?.length || 11;
        
        // If no question exists or we're past the last question, complete session
        if (!question || currentQuestion >= totalQuestions) {
          console.log('Watchdog: Session appears complete, showing summary');
          setShowSummary(true);
          refreshUser();
        }
      }
    }, 5000); // Increased from 2s to 5s to give memory games more time
    
    return () => clearTimeout(watchdog);
  }, [currentQuestion, session, loading, showSummary, questionLoadTime, refreshUser]);

  // 1) Financial Mission — shows FIRST for new users, stays until dismissed
  if (showFinancialMission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="bg-white rounded-3xl p-6 shadow-2xl animate-pop">
            <div className="text-6xl mb-4">🚀</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3 font-fredoka">Financial Mission</h2>
            <div className="text-left space-y-3 mb-6">
              <p className="text-slate-700">
                <span className="font-bold text-primary">Master Your Coins!</span> Every quiz you solve earns you coins—just like real-life earning!
              </p>
              <p className="text-slate-700">
                But here is the secret <span className="font-bold text-purple-600">BrainGym Puzzle</span>: don't just spend them. Use your logical thinking to add a <span className="font-bold">"Smart Funnel"</span> to your money.
              </p>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 border border-green-200">
                <p className="text-slate-800 font-medium">
                  First, <span className="text-green-600 font-bold">Invest</span> to grow your coins and <span className="text-blue-600 font-bold">Save</span> for big goals or emergencies.
                </p>
                <p className="text-slate-800 font-medium mt-2">
                  Then, <span className="text-amber-600 font-bold">Spend</span> only what is left or the extra coins you grew!
                </p>
              </div>
              <p className="text-slate-600 text-sm italic">
                This is a <span className="font-bold">Real-Life Brain Skill</span> that helps you make "Best Buy" decisions for your future.
              </p>
            </div>
            <button
              onClick={handleDismissFinancialMission}
              className="w-full py-3 bg-gradient-to-r from-primary to-amber-400 rounded-2xl font-bold text-black text-lg hover:shadow-lg transition-all"
              data-testid="dismiss-financial-mission"
            >
              Got it! Let's Start! 🧠
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 2) DJ MODE PICKER — shows AFTER Financial Mission is dismissed
  if (showDjMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          {/* DJ Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
              <Disc3 className="w-5 h-5 text-purple-300 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-purple-200 font-medium text-sm">DJ MODE</span>
              <Music className="w-4 h-4 text-purple-300" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-white mb-2" data-testid="dj-mode-title">
              Mix Your Session
            </h1>
            <p className="text-purple-200/80 text-sm">
              Pick <span className="font-bold text-white">3 tracks</span> from the crate below
            </p>
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6" data-testid="dj-category-grid">
            {djCategories.map((cat) => {
              const isSelected = djPicks.includes(cat.id);
              const config = CATEGORY_CONFIG[cat.id] || { icon: '✨', color: 'bg-slate-100 text-slate-700' };
              return (
                <button
                  key={cat.id}
                  data-testid={`dj-category-${cat.id}`}
                  onClick={() => handleDjToggle(cat.id)}
                  disabled={!isSelected && djPicks.length >= 3}
                  className={`relative rounded-xl p-4 text-left transition-all duration-200 border-2 ${
                    isSelected
                      ? 'bg-purple-500/30 border-purple-400 shadow-lg shadow-purple-500/20 scale-[1.02]'
                      : djPicks.length >= 3
                      ? 'bg-white/5 border-white/10 opacity-40 cursor-not-allowed'
                      : 'bg-white/10 border-white/10 hover:bg-white/15 hover:border-white/30'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="text-2xl mb-1">{config.icon}</div>
                  <div className="text-white font-bold text-sm leading-tight">{cat.name}</div>
                  <div className="text-purple-200/60 text-xs mt-1 line-clamp-2">{cat.description}</div>
                </button>
              );
            })}
          </div>

          {/* Selection Counter */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  i < djPicks.length ? 'bg-purple-400 scale-110' : 'bg-white/20'
                }`}
              />
            ))}
            <span className="text-purple-200/70 text-xs ml-2">{djPicks.length}/3 selected</span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              data-testid="dj-confirm-btn"
              onClick={handleDjConfirm}
              disabled={djPicks.length !== 3 || djLoading}
              className={`w-full py-3.5 rounded-xl font-bold text-lg transition-all duration-200 ${
                djPicks.length === 3
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 active:scale-[0.98]'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
            >
              {djLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Disc3 className="w-5 h-5 animate-spin" /> Mixing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" /> Drop the Mix
                </span>
              )}
            </button>
            <button
              data-testid="dj-skip-btn"
              onClick={handleDjSkip}
              className="w-full py-2.5 rounded-xl text-purple-300/70 text-sm hover:text-white transition-colors"
            >
              Skip — surprise me
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3) Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          {/* Animated brain icon */}
          <div className="relative w-32 h-32 mx-auto mb-6">
            <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
            <div className="absolute inset-2 bg-white/30 rounded-full animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-6xl animate-bounce">🧠</span>
            </div>
          </div>
          
          {/* Loading text */}
          <h2 className="text-2xl font-bold text-white mb-2 font-fredoka">
            Preparing Your Puzzles!
          </h2>
          <p className="text-white/80 mb-6">Get ready to train your brain...</p>
          
          {/* Beat Yesterday Popup - shows during loading */}
          {showBeatYesterday && yesterdayScore && (
            <div data-testid="beat-yesterday-popup" className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 mb-6 shadow-xl animate-pop border-2 border-amber-300">
              <div className="text-3xl mb-1">🔥⭐🔥</div>
              <p className="text-amber-800 font-black text-lg font-fredoka mb-1">Beat Yesterday!</p>
              <p className="text-slate-700 text-sm">
                Your score: <span className="font-bold text-amber-600">⭐ {yesterdayScore.correct}/{yesterdayScore.total}</span>
              </p>
              <p className="text-indigo-600 font-bold text-sm mt-1">💪 Can you beat it today? 🎯</p>
              <div className="mt-2 bg-amber-100 rounded-full px-3 py-1 inline-block">
                <span className="text-xs font-bold text-amber-700">🪙 +10 bonus coins if you do!</span>
              </div>
            </div>
          )}

          {/* Animated dots */}
          <div className="flex justify-center gap-2">
            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  if (showSummary) {
    return (
      <SessionSummary
        answers={answers}
        coinsEarned={coinsEarned}
        totalTime={QUIZ_DURATION - remainingTime}
        onContinue={() => navigate('/dashboard')}
        beatYesterdayBonus={beatYesterdayBonus}
        yesterdayScore={yesterdayScore}
      />
    );
  }

  const question = session?.questions?.[currentQuestion];
  if (!question) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p>Loading question...</p>
      </div>
    );
  }

  const totalQuestions = session?.total_questions || session?.questions?.length || 11;
  const completedQuestions = answers.length;
  const progressPercent = (completedQuestions / totalQuestions) * 100;
  
  const categoryConfig = CATEGORY_CONFIG[question.category] || CATEGORY_CONFIG.logic;

  const renderQuestion = () => {
    // Memory Grid Game
    if (question.type === 'memory_grid') {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{question.title}</h3>
            <p className="text-slate-600">{question.instruction}</p>
            {memoryPhase === 'show' && (
              <p className="text-primary font-bold mt-2">Memorize the cards!</p>
            )}
          </div>
          
          <div className={`grid gap-3 justify-center mx-auto max-w-md ${
            question.grid_size <= 2 ? 'grid-cols-4' : question.grid_size <= 3 ? 'grid-cols-4' : 'grid-cols-4'
          }`}>
            {memoryCards.map((card) => (
              <button
                key={card.id}
                onClick={() => handleCardFlip(card.id)}
                disabled={memoryPhase === 'show' || matchedPairs.includes(card.id)}
                className={`w-16 h-16 rounded-xl text-2xl font-bold transition-all transform ${
                  card.flipped || matchedPairs.includes(card.id)
                    ? 'bg-primary text-white scale-105'
                    : 'bg-slate-200 hover:bg-slate-300'
                } ${matchedPairs.includes(card.id) ? 'bg-green-500' : ''}`}
                data-testid={`memory-card-${card.id}`}
              >
                {(card.flipped || matchedPairs.includes(card.id)) ? card.value : '?'}
              </button>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-slate-500">
              Pairs found: {matchedPairs.length / 2} / {question.pairs_count}
            </p>
            {memoryGameComplete && (
              <div className="mt-4 space-y-2">
                <p className="text-green-600 font-bold text-lg">All pairs matched!</p>
                <div className="flex items-center justify-center gap-2 text-slate-500">
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  <span>Moving to next question...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Memory Recall Game
    if (question.type === 'memory_recall') {
      if (memoryPhase === 'memorize') {
        return (
          <div className="space-y-6 text-center">
            <h3 className="text-2xl font-bold text-slate-900">{question.title}</h3>
            <p className="text-slate-600">{question.instruction}</p>
            
            <div className="text-6xl font-bold text-primary">{memoryTimer}</div>
            
            <div className="flex flex-wrap justify-center gap-4 p-6 bg-slate-50 rounded-2xl">
              {question.items_to_show?.map((item, idx) => (
                <div key={idx} className="text-3xl p-4 bg-white rounded-xl shadow">
                  {item}
                </div>
              ))}
            </div>
            
            <p className="text-slate-500 font-medium">Remember these items!</p>
          </div>
        );
      }
      
      // Answer phase - same as standard question
    }

    // Focus Question with Items to Count
    if (question.type === 'focus' && question.items) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{question.question}</h3>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2 p-6 bg-slate-50 rounded-2xl">
            {question.items.map((item, idx) => (
              <span key={idx} className="text-3xl">{item}</span>
            ))}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {question.options?.map((option, idx) => {
              const isSelected = selectedAnswer !== null && selectedAnswer === option;
              const shouldShowResult = showResult && selectedAnswer !== null;
              
              // NO GREEN HIGHLIGHTING
              let buttonClass = 'bg-slate-100 hover:bg-slate-200';
              
              if (isSelected && shouldShowResult) {
                buttonClass = isCorrect ? 'bg-primary text-white' : 'bg-red-500 text-white';
              } else if (isSelected && !shouldShowResult) {
                buttonClass = 'bg-primary text-white scale-105';
              }
              
              return (
                <button
                  key={`q${currentQuestion}-focus-items-${idx}`}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={shouldShowResult}
                  className={`p-4 rounded-xl text-lg font-bold transition-all ${buttonClass}`}
                  data-testid={`option-${idx}`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Focus Question with Equations
    if (question.type === 'focus' && question.equations) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{question.question}</h3>
          </div>
          
          <div className="space-y-3 p-4 bg-slate-50 rounded-2xl">
            {question.equations.map((eq, idx) => (
              <div key={idx} className="text-xl font-mono text-center py-2 bg-white rounded-lg">
                {eq}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {question.options?.map((option, idx) => {
              const isSelected = selectedAnswer !== null && selectedAnswer === option;
              const shouldShowResult = showResult && selectedAnswer !== null;
              
              // NO GREEN HIGHLIGHTING
              let buttonClass = 'bg-slate-100 hover:bg-slate-200';
              
              if (isSelected && shouldShowResult) {
                buttonClass = isCorrect ? 'bg-primary text-white' : 'bg-red-500 text-white';
              } else if (isSelected && !shouldShowResult) {
                buttonClass = 'bg-primary text-white scale-105';
              }
              
              return (
                <button
                  key={`q${currentQuestion}-focus-eq-${idx}`}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={shouldShowResult}
                  className={`p-4 rounded-xl text-base font-bold transition-all ${buttonClass}`}
                  data-testid={`option-${idx}`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Standard Question (logic, pattern, speed, memory_recall answer phase)
    return (
      <div className="space-y-6">
        <div className="bg-slate-50 rounded-2xl p-6">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 text-center">
            {question.question || question.title}
          </h3>
          
          {/* Show sequence for pattern questions */}
          {question.sequence && (
            <div className="flex justify-center items-center gap-3 mt-4 flex-wrap">
              {question.sequence.map((item, idx) => (
                <span key={idx} className={`text-2xl md:text-3xl ${item === '?' ? 'text-primary font-bold' : ''}`}>
                  {item}
                </span>
              ))}
            </div>
          )}
          
          {/* Speed indicator */}
          {question.category === 'speed' && question.time_limit && (
            <div className="text-center mt-2">
              <span className="text-sm text-orange-600 font-bold">Quick! Answer fast!</span>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {question.options?.map((option, idx) => {
            const isSelected = selectedAnswer !== null && selectedAnswer === option;
            const shouldShowResult = showResult && selectedAnswer !== null;
            
            // Default to neutral state - NO GREEN HIGHLIGHTING
            let buttonClass = 'bg-slate-100 hover:bg-slate-200';
            
            if (isSelected && shouldShowResult) {
              // Show result: primary color for correct, red for wrong
              buttonClass = isCorrect ? 'bg-primary text-white' : 'bg-red-500 text-white';
            } else if (isSelected && !shouldShowResult) {
              // Selected but not submitted yet
              buttonClass = 'bg-primary text-white scale-105';
            }
            // No green highlight for correct answer - removed completely
            
            return (
              <button
                key={`q${currentQuestion}-opt${idx}`}
                onClick={() => handleAnswerSelect(option)}
                disabled={shouldShowResult}
                className={`p-4 rounded-xl text-lg font-bold transition-all ${buttonClass}`}
                data-testid={`option-${idx}`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="session-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{categoryConfig.icon}</span>
              <div>
                <h2 className="font-bold text-slate-900 font-fredoka text-xl">
                  {categoryConfig.name}
                </h2>
                <p className="text-sm text-slate-500">
                  Question {currentQuestion + 1} of {totalQuestions}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-full font-mono font-bold transition-colors ${
                remainingTime <= 60
                  ? 'bg-red-100 text-red-700 animate-pulse'
                  : remainingTime <= 120
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-700'
              }`} data-testid="quiz-timer">
                <Clock className="w-4 h-4" />
                <span>{formatTime(remainingTime)}</span>
              </div>
              
              <button
                onClick={handleExit}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                data-testid="exit-session-btn"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>
          </div>
          
          <Progress value={progressPercent} className="h-2 rounded-full bg-slate-100" />
        </div>
      </header>

      {/* Daily Theme Banner */}
      {session?.theme && (
        <div className="max-w-4xl mx-auto px-4 pt-3">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl px-4 py-2 flex items-center gap-2 border border-indigo-100" data-testid="daily-theme-banner">
            <span className="text-xl">{session.theme.emoji}</span>
            <span className="text-sm font-bold text-indigo-700">Today's Theme: {session.theme.name}</span>
          </div>
        </div>
      )}

      {/* Mentor Message */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="bg-secondary/10 rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="mentor-message">
          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <p className="text-slate-700 font-medium">{mentorMessage}</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          {/* Category Badge */}
          <div className="flex justify-center mb-4">
            <span className={`px-4 py-1 rounded-full text-sm font-bold ${categoryConfig.color}`}>
              {categoryConfig.icon} {categoryConfig.name}
            </span>
          </div>
          
          {renderQuestion()}
          
          {/* Hint Section */}
          {question.hints && question.type !== 'memory_grid' && !showResult && (
            <div className="mt-6">
              {showHint && hintsUsed > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                  <p className="text-yellow-800 font-medium">
                    💡 Hint {hintsUsed}: {question.hints[hintsUsed - 1]}
                  </p>
                </div>
              )}
              
              {hintsUsed < 3 && (
                <button
                  onClick={handleShowHint}
                  className="text-slate-500 hover:text-primary transition-colors text-sm flex items-center gap-1"
                  data-testid="hint-btn"
                >
                  <Lightbulb className="w-4 h-4" />
                  Need a hint? ({3 - hintsUsed} left)
                </button>
              )}
            </div>
          )}
          
          {/* Submit Button - Not for memory grid */}
          {question.type !== 'memory_grid' && (
            <div className="mt-6">
              {!showResult ? (
                <button
                  onClick={handleSubmitAnswer}
                  disabled={!selectedAnswer || isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-answer-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      Check Answer <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-3" data-testid="answer-result">
                  {/* Minimal result banner */}
                  <div className={`flex items-center gap-3 px-5 py-4 rounded-xl ${
                    isCorrect 
                      ? 'bg-emerald-50 border border-emerald-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                    }`}>
                      {isCorrect 
                        ? <CheckCircle2 className="w-5 h-5 text-white" /> 
                        : <XCircle className="w-5 h-5 text-white" />
                      }
                    </div>
                    <div className="min-w-0">
                      <p className={`font-bold text-base ${isCorrect ? 'text-emerald-800' : 'text-red-800'}`}>
                        {isCorrect ? 'Correct!' : 'Not quite'}
                      </p>
                      {!isCorrect && (
                        <p className="text-sm text-red-600 mt-0.5 truncate">
                          Answer: <span className="font-semibold">{question.correct_answer}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Fun fact — correct answers only */}
                  {isCorrect && question.fun_fact && (
                    <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
                      <p className="text-xs text-slate-600">
                        <span className="font-bold text-slate-700">Fun fact — </span>{question.fun_fact}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-testid="exit-modal">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Exit Session?</h3>
            <p className="text-slate-600 mb-2">Your progress is saved! You can continue from where you left off later.</p>
            <p className="text-amber-600 text-sm font-medium mb-6 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              The 10-minute timer will keep running!
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-slate-700 transition-colors"
                data-testid="cancel-exit-btn"
              >
                Continue
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 py-3 px-4 bg-primary hover:bg-primary/90 rounded-xl font-bold text-white transition-colors"
                data-testid="confirm-exit-btn"
              >
                Exit & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionPage;
